import { investorRemoved } from '@/store/features/investors/investorsSlice';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// ==================== Type Definitions ====================

export interface GoalSubItem {
  name: string;
  checked: boolean;
  amount: string;
  year?: string;
}

export interface GoalItem {
  name: string;
  checked: boolean;
  amount: string;
  details?: string;
  year?: string;
  // For parent goals with sub-options (e.g., Major Purchase)
  parentCategory?: string;
  subGoals?: GoalSubItem[];
}

export interface GoalsChecklistValue {
  goals: GoalItem[];
}

export interface AssetAllocationItem {
  name: string;
  percentage: string;
}

export interface AssetAllocationValue {
  allocations: AssetAllocationItem[];
}

export interface PlannedWithdrawalValue {
  amount: string;
  startYear: string;
  frequency: 'monthly' | 'quarterly' | 'semi-annually' | 'annually' | 'one-time';
}

export type LifeInsuranceCoverageType =
  | 'term'
  | 'whole'
  | 'universal'
  | 'variable'
  | 'group'
  | 'other';

export interface LifeInsuranceCoverageValue {
  amount: string;
  coverageType: LifeInsuranceCoverageType;
  otherTypeDescription?: string;
}

export interface SpouseInfoValue {
  hasSpouse: boolean;
  selectedSpouseId?: string;
  name?: string;
  dateOfBirth?: string;
  income?: string;
  benefits?: string;
}

export type DeductionType = 'standard' | 'itemized';

export interface TaxAssumptionsValue {
  federalMarginalRate: string;
  stateMarginalRate: string;
  deductionType: DeductionType;
  capitalGainsRate?: string;
}

export interface AssumptionField {
  label: string;
  name: string;
  value:
    | string
    | number
    | GoalsChecklistValue
    | AssetAllocationValue
    | PlannedWithdrawalValue
    | LifeInsuranceCoverageValue
    | SpouseInfoValue
    | TaxAssumptionsValue;
  type:
    | 'input'
    | 'select'
    | 'textarea'
    | 'date'
    | 'number'
    | 'goals-checklist'
    | 'asset-allocation'
    | 'planned-withdrawal'
    | 'life-insurance'
    | 'spouse-info'
    | 'life-expectancy'
    | 'tax-assumptions';
  options?: string[];
  placeholder?: string;
  required: boolean;
  inputType?: 'text' | 'number' | 'currency' | 'percentage';
}

export interface SelectedFile {
  s3Key: string;
  title: string;
  sizeInBytes?: number;
}
export const SelectorMode = {
  ExistingInvestor: 'existing-investor',
  ExistingClient: 'existing-investor',
  NewInvestor: 'new-investor',
  NewClient: 'new-investor',
  NotSelected: 'not-selected',
} as const;

export type SelectorMode = (typeof SelectorMode)[keyof typeof SelectorMode];

export interface InvestorSelector {
  selectorMode: SelectorMode;
  selectedInvestor?: {
    id: string; // Database ID (always exists)
    cognitoId?: string; // Cognito ID (may not exist)
    name?: string | null;
  };
  newInvestorDetails?: {
    name?: string;
    dateOfBirth?: string;
    phoneNumber?: string;
    email?: string;
    goals?: string[];
    personalizedMessage?: string;
  };
}

export interface ClientSelector {
  selectorMode: SelectorMode;
  selectedCustomer?: InvestorSelector['selectedInvestor'];
  newClientDetails?: InvestorSelector['newInvestorDetails'];
}

export interface TemplateItem {
  templateId: string;
  displayName: string;
  displayDescription: string;
  templateSourceCode: string;
}

export interface TemplateSelector {
  selectedTemplate?: TemplateItem;
  templates: TemplateItem[];
  isLoading: boolean;
  error: string | null;
  lastFetchTime: number | null;
}

export interface ContextChecklist {
  assumptions: AssumptionField[];
  viewingDataSource?: 'plaid' | 'apex' | null;
  selectedDataSources: {
    plaid: boolean;
    apex: boolean;
  };
  selectedFiles: SelectedFile[];
  selectedLocalFiles: File[];
}

// ==================== State Structure ====================

export interface GuidedModeState {
  currentStep: number;
  investorSelector: InvestorSelector;
  clientSelector: ClientSelector;
  templateSelector: TemplateSelector;
  contextChecklist: ContextChecklist;
  activeV2CategoryKey: string | null;
  activeV2CategoryName: string | null;
  activeV2PromptName: string | null;
  v2NavigateBackToken: number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface SandboxModeState {
  // Placeholder for sandbox mode specific state
  // Will be populated during sandbox mode migration
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface SharedCommonState {
  // Placeholder for shared common state
  // Will be populated during refactoring
}

export interface PlanningState {
  guidedMode: GuidedModeState;
  sandboxMode: SandboxModeState;
  shared: SharedCommonState;
}

// ==================== Initial State ====================

const toClientSelector = (investorSelector: InvestorSelector): ClientSelector => ({
  selectorMode: investorSelector.selectorMode,
  selectedCustomer: investorSelector.selectedInvestor,
  newClientDetails: investorSelector.newInvestorDetails,
});

const syncLegacyClientSelector = (guidedMode: GuidedModeState) => {
  guidedMode.clientSelector = toClientSelector(guidedMode.investorSelector);
};

const resetDataSourceContext = (guidedMode: GuidedModeState) => {
  guidedMode.contextChecklist.selectedDataSources = {
    plaid: false,
    apex: false,
  };
  guidedMode.contextChecklist.viewingDataSource = null;
};

const initialInvestorSelector: InvestorSelector = {
  selectorMode: SelectorMode.ExistingInvestor,
};

const initialGuidedModeState: GuidedModeState = {
  currentStep: 0,
  activeV2CategoryKey: null,
  activeV2CategoryName: null,
  activeV2PromptName: null,
  v2NavigateBackToken: 0,
  investorSelector: initialInvestorSelector,
  clientSelector: toClientSelector(initialInvestorSelector),
  templateSelector: {
    templates: [],
    isLoading: false,
    error: null,
    lastFetchTime: null,
  },
  contextChecklist: {
    assumptions: [],
    viewingDataSource: null,
    selectedDataSources: {
      plaid: false,
      apex: false,
    },
    selectedFiles: [],
    selectedLocalFiles: [],
  },
};

const initialSandboxModeState: SandboxModeState = {
  // Will be populated during sandbox mode migration
};

const initialSharedCommonState: SharedCommonState = {
  // Will be populated during refactoring
};

const initialState: PlanningState = {
  guidedMode: initialGuidedModeState,
  sandboxMode: initialSandboxModeState,
  shared: initialSharedCommonState,
};

// ==================== Slice ====================

export const planningSlice = createSlice({
  name: 'planning',
  initialState,
  reducers: {
    // ========== Guided Mode Step Management ==========
    setGuidedModeStep: (state, action: PayloadAction<number>) => {
      state.guidedMode.currentStep = action.payload;
    },
    incrementGuidedModeStep: (state) => {
      state.guidedMode.currentStep += 1;
    },
    decrementGuidedModeStep: (state) => {
      state.guidedMode.currentStep -= 1;
    },
    resetGuidedModeStep: (state) => {
      state.guidedMode.currentStep = 0;
    },

    // ========== Investor Selector Management ==========
    updateInvestorSelector: (
      state,
      action: PayloadAction<Omit<Partial<InvestorSelector>, 'selectorMode'>>,
    ) => {
      const selectorMode = state.guidedMode.investorSelector.selectorMode;
      state.guidedMode.investorSelector = {
        ...state.guidedMode.investorSelector,
        ...action.payload,
        selectorMode,
      };
      syncLegacyClientSelector(state.guidedMode);
    },
    setInvestorSelectorMode: (state, action: PayloadAction<InvestorSelector['selectorMode']>) => {
      const newMode = action.payload;
      state.guidedMode.investorSelector.selectorMode = newMode;

      // Clear mode-specific data when switching
      if (newMode === SelectorMode.NewInvestor) {
        // When switching to new investor mode, clear existing investor data
        state.guidedMode.investorSelector.selectedInvestor = undefined;
      } else if (newMode === SelectorMode.ExistingInvestor) {
        // When switching to existing investor mode, clear new investor data
        state.guidedMode.investorSelector.newInvestorDetails = undefined;
      } else if (newMode === SelectorMode.NotSelected) {
        state.guidedMode.investorSelector.selectedInvestor = undefined;
        state.guidedMode.investorSelector.newInvestorDetails = undefined;
      }

      // Clear data sources selection for both modes to prevent data pollution
      resetDataSourceContext(state.guidedMode);
      syncLegacyClientSelector(state.guidedMode);
    },
    setSelectedInvestor: (state, action: PayloadAction<InvestorSelector['selectedInvestor']>) => {
      state.guidedMode.investorSelector.selectorMode = action.payload
        ? SelectorMode.ExistingInvestor
        : SelectorMode.NotSelected;
      state.guidedMode.investorSelector.newInvestorDetails = undefined;
      state.guidedMode.investorSelector.selectedInvestor = action.payload;
      resetDataSourceContext(state.guidedMode);
      syncLegacyClientSelector(state.guidedMode);
    },
    updateNewInvestorDetails: (
      state,
      action: PayloadAction<Partial<InvestorSelector['newInvestorDetails']>>,
    ) => {
      state.guidedMode.investorSelector.selectorMode = SelectorMode.NewInvestor;
      state.guidedMode.investorSelector.selectedInvestor = undefined;
      state.guidedMode.investorSelector.newInvestorDetails = {
        ...state.guidedMode.investorSelector.newInvestorDetails,
        ...action.payload,
      };
      resetDataSourceContext(state.guidedMode);
      syncLegacyClientSelector(state.guidedMode);
    },

    // ========== Template Selector Management ==========
    setSelectedTemplate: (state, action: PayloadAction<TemplateItem | undefined>) => {
      state.guidedMode.templateSelector.selectedTemplate = action.payload;
    },
    setTemplatesLoading: (state, action: PayloadAction<boolean>) => {
      state.guidedMode.templateSelector.isLoading = action.payload;
    },
    setTemplatesError: (state, action: PayloadAction<string | null>) => {
      state.guidedMode.templateSelector.error = action.payload;
      state.guidedMode.templateSelector.isLoading = false;
    },
    setTemplates: (state, action: PayloadAction<TemplateItem[]>) => {
      state.guidedMode.templateSelector.templates = action.payload;
      state.guidedMode.templateSelector.lastFetchTime = Date.now();
      state.guidedMode.templateSelector.isLoading = false;
      state.guidedMode.templateSelector.error = null;
    },
    clearTemplatesCache: (state) => {
      state.guidedMode.templateSelector.templates = [];
      state.guidedMode.templateSelector.lastFetchTime = null;
    },

    // ========== Context Checklist Management ==========
    setAssumptions: (state, action: PayloadAction<AssumptionField[]>) => {
      state.guidedMode.contextChecklist.assumptions = action.payload;
    },
    setViewingDataSource: (state, action: PayloadAction<'plaid' | 'apex' | null>) => {
      state.guidedMode.contextChecklist.viewingDataSource = action.payload;
    },
    setSelectedDataSources: (state, action: PayloadAction<{ plaid: boolean; apex: boolean }>) => {
      state.guidedMode.contextChecklist.selectedDataSources = action.payload;
    },
    setSelectedFiles: (state, action: PayloadAction<SelectedFile[]>) => {
      state.guidedMode.contextChecklist.selectedFiles = action.payload;
    },
    setSelectedLocalFiles: (state, action: PayloadAction<File[]>) => {
      state.guidedMode.contextChecklist.selectedLocalFiles = action.payload;
    },

    // ========== Active V2 Category / Prompt ==========
    setActiveV2CategoryContext: (
      state,
      action: PayloadAction<{
        key: string | null;
        name: string | null;
        promptName: string | null;
      }>,
    ) => {
      state.guidedMode.activeV2CategoryKey = action.payload.key;
      state.guidedMode.activeV2CategoryName = action.payload.name;
      state.guidedMode.activeV2PromptName = action.payload.promptName;
    },
    clearActiveV2CategoryContext: (state) => {
      state.guidedMode.activeV2CategoryKey = null;
      state.guidedMode.activeV2CategoryName = null;
      state.guidedMode.activeV2PromptName = null;
    },
    incrementV2NavigateBackToken: (state) => {
      state.guidedMode.v2NavigateBackToken += 1;
    },

    // ========== Reset Guided Mode ==========
    resetGuidedMode: (state) => {
      state.guidedMode = initialGuidedModeState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(investorRemoved, (state, action) => {
      if (state.guidedMode.investorSelector.selectedInvestor?.id === action.payload) {
        state.guidedMode = initialGuidedModeState;
      }
    });
  },
});

// ==================== Actions ====================

export const {
  setGuidedModeStep,
  incrementGuidedModeStep,
  decrementGuidedModeStep,
  resetGuidedModeStep,
  updateInvestorSelector,
  setInvestorSelectorMode,
  setSelectedInvestor,
  updateNewInvestorDetails,
  setSelectedTemplate,
  setTemplatesLoading,
  setTemplatesError,
  setTemplates,
  clearTemplatesCache,
  setAssumptions,
  setViewingDataSource,
  setSelectedDataSources,
  setSelectedFiles,
  setSelectedLocalFiles,
  setActiveV2CategoryContext,
  clearActiveV2CategoryContext,
  incrementV2NavigateBackToken,
  resetGuidedMode,
} = planningSlice.actions;

export const setClientSelectorMode = setInvestorSelectorMode;
export const setSelectedCustomer = setSelectedInvestor;
export const updateNewClientDetails = updateNewInvestorDetails;

export default planningSlice.reducer;

// ==================== Selectors ====================

export const selectGuidedModeStep = (state: { planning: PlanningState }) =>
  state.planning.guidedMode.currentStep;

export const selectInvestorSelector = (state: { planning: PlanningState }) =>
  state.planning.guidedMode.investorSelector;

export const selectClientSelector = (state: { planning: PlanningState }) =>
  state.planning.guidedMode.clientSelector;

export const selectTemplateSelector = (state: { planning: PlanningState }) =>
  state.planning.guidedMode.templateSelector;

export const selectContextChecklist = (state: { planning: PlanningState }) =>
  state.planning.guidedMode.contextChecklist;

export const selectActiveV2CategoryKey = (state: { planning: PlanningState }) =>
  state.planning.guidedMode.activeV2CategoryKey;

export const selectActiveV2CategoryName = (state: { planning: PlanningState }) =>
  state.planning.guidedMode.activeV2CategoryName;

export const selectActiveV2PromptName = (state: { planning: PlanningState }) =>
  state.planning.guidedMode.activeV2PromptName;

export const selectV2NavigateBackToken = (state: { planning: PlanningState }) =>
  state.planning.guidedMode.v2NavigateBackToken;
